import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  CreditCard,
  QrCode,
  CheckCircle,
  Search,
  Trash2,
  Download,
} from "lucide-react";
import { toast } from "sonner";

interface GiftCardIssuanceResponse {
  id: number;
  giftCardId: number;
  name: string;
  mobileNo: string | null;
  email: string;
  amount: number;
  expiry: string;
  isSent: boolean;
  sendOn: string;
  isActive: boolean;
  createdBy: number;
  createdDateTime: string;
  qrCode: string | null;
  issuedCardNumber: string;
  personalMessage: string | null;
  occasion: string | null;
  createdByName: string | null;
  returnData: string;
  error_msg: string;
}

interface GiftCardFormData {
  selectedGiftCardId: string;
  amount: string;
  recipientName: string;
  recipientEmail: string;
  recipientMobile: string;
  personalMessage: string;
  occasion: string;
}

interface GiftCardTemplate {
  id: number;
  title: string;
  description: string;
  amount: number;
  bannerImage: string;
  bannerImagePath: string;
  isActive: boolean;
  createdBy: number;
  createdDateTime: string;
}

// Yup validation schema
const validationSchema = Yup.object({
  selectedGiftCardId: Yup.string().required("Please select a gift card"),
  recipientName: Yup.string()
    .required("Recipient name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name cannot exceed 100 characters")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  recipientEmail: Yup.string()
    .required("Recipient email is required")
    .email("Please enter a valid email address"),
  recipientMobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]+$/, "Mobile number must contain only digits")
    .min(7, "Mobile number must be at least 7 digits")
    .max(15, "Mobile number must not exceed 15 digits"),
  personalMessage: Yup.string().max(
    500,
    "Personal message cannot exceed 500 characters"
  ),
  occasion: Yup.string().max(50, "Occasion cannot exceed 50 characters"),
});

const IssueGiftCard = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isGiftCardModalOpen, setIsGiftCardModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGiftCard, setSelectedGiftCard] =
    useState<GiftCardTemplate | null>(null);
  const [giftCardTemplates, setGiftCardTemplates] = useState<
    GiftCardTemplate[]
  >([]);
  const [isLoadingGiftCards, setIsLoadingGiftCards] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [totalGiftCards, setTotalGiftCards] = useState(0);
  const [issuedGiftCard, setIssuedGiftCard] =
    useState<GiftCardIssuanceResponse | null>(null);
  const [formResetFn, setFormResetFn] = useState<(() => void) | null>(null);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);

  const hasInitialFetchRef = useRef(false);

  const totalPages = Math.max(
    1,
    Math.ceil((totalGiftCards || 0) / (rowsPerPage || 1))
  );

  const fetchGiftCards = async (options?: {
    page?: number;
    pageSize?: number;
    mallId?: number | null;
  }) => {
    setIsLoadingGiftCards(true);
    try {
      // Get userId from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const userId = userInfo ? JSON.parse(userInfo).userId : 1;

      const currentPage = options?.page ?? page;
      const currentPageSize = options?.pageSize ?? rowsPerPage;
      const mallIdToUse =
        options?.mallId !== undefined ? options.mallId : selectedMallId ?? 0;

      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/GetAllGiftCard`,
        {
          p_Id: userId,
          p_MallId: mallIdToUse,
          // Backend expects 1-based page index
          p_Page: currentPage + 1,
          p_PageSize: currentPageSize,
        },
        {
          headers: {
            XApiKey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.giftCards) {
        setGiftCardTemplates(response.data.giftCards);
        setTotalGiftCards(
          response.data.totalCount ?? response.data.giftCards.length ?? 0
        );
      }
    } catch (error) {
      console.error("Error fetching gift cards:", error);
      toast.error("Failed to load gift cards. Please try again.");
    } finally {
      setIsLoadingGiftCards(false);
    }
  };

  const fetchMallOptions = useCallback(async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMalls(mapped);

      if (mapped.length > 0) {
        const firstMallId = mapped[0].id;
        setSelectedMallId(firstMallId);
        hasInitialFetchRef.current = true;
        await fetchGiftCards({
          page: 0,
          pageSize: rowsPerPage,
          mallId: firstMallId,
        });
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      toast.error("Failed to load mall list");
      setSelectedMallId(null);
      hasInitialFetchRef.current = true;
      await fetchGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: 0,
      });
    } finally {
      setIsMallLoading(false);
    }
  }, []);

  // Fetch malls + gift cards when modal opens
  useEffect(() => {
    if (isGiftCardModalOpen && !hasInitialFetchRef.current) {
      fetchMallOptions();
    }
  }, [isGiftCardModalOpen, fetchMallOptions]);

  const filteredGiftCards = giftCardTemplates.filter(
    (card) =>
      card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage > totalPages - 1) return;
    setPage(newPage);
    fetchGiftCards({
      page: newPage,
      pageSize: rowsPerPage,
      mallId: selectedMallId,
    });
  };

  const initialValues: GiftCardFormData = {
    selectedGiftCardId: "",
    amount: "",
    recipientName: "",
    recipientEmail: "",
    recipientMobile: "",
    personalMessage: "",
    occasion: "",
  };

  const handleSubmit = async (values: GiftCardFormData) => {
    setIsSubmitting(true);

    try {
      // Get userId and username from sessionStorage
      const userInfoStr = sessionStorage.getItem("userInfo");
      const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
      const userId = userInfo?.userId || 1;
      const userName = userInfo?.username || "";

      // Prepare API payload
      const payload = {
        p_GiftCardId: parseInt(values.selectedGiftCardId),
        p_Name: values.recipientName,
        p_MobileNo: values.recipientMobile,
        p_Email: values.recipientEmail,
        p_Amount: parseFloat(values.amount),
        p_Expiry: "2025-12-31T05:52:43.596Z",
        p_IsSent: true,
        p_SendOn: "Email",
        p_IsActive: true,
        p_CreatedBy: userId,
        p_PersonalMessage: values.personalMessage,
        p_Occasion: values.occasion,
        p_UserName: userName,
      };

      // Call issuance API
      const response = await axios.post<GiftCardIssuanceResponse>(
        `${config.GIFT_API_BASE_URL}/GiftCard/GiftCardIssuance`,
        payload,
        {
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.error_msg) {
        toast.error(response.data.error_msg);
        return;
      }

      // Set the issued gift card data from API response
      setIssuedGiftCard(response.data);

      // Show success modal
      setIsSuccessModalOpen(true);

      toast.success("Gift card issued successfully!");
    } catch (error) {
      console.error("Error issuing gift card:", error);
      toast.error("Failed to issue gift card. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/giftcard/allissuedgiftcards");
  };

  const handleGiftCardSelect = (card: GiftCardTemplate, setFieldValue: any) => {
    setSelectedGiftCard(card);
    setFieldValue("selectedGiftCardId", card.id.toString());
    setFieldValue("amount", card.amount.toString());
    setIsGiftCardModalOpen(false);
    setSearchTerm("");
  };

  const handleRemoveGiftCard = (setFieldValue: any) => {
    setSelectedGiftCard(null);
    setFieldValue("selectedGiftCardId", "");
    setFieldValue("amount", "");
  };

  // Function to download QR code via API
  const downloadQRCode = async () => {
    if (!issuedGiftCard) {
      toast.error("No gift card selected");
      return;
    }

    const rawCardNumber = issuedGiftCard.issuedCardNumber || "";
    const sanitizedCardNumber = rawCardNumber.replace(/\s+/g, "");
    if (!sanitizedCardNumber) {
      toast.error("Card number not available for QR download.");
      return;
    }

    // Get username from sessionStorage (same pattern as other gift APIs)
    const userInfoStr = sessionStorage.getItem("userInfo");
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    const userName = userInfo?.username || "";

    try {
      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/DownloadQRCode`,
        JSON.stringify({
          userName,
          cardNumber: sanitizedCardNumber,
        }),
        {
          headers: {
            XApiKey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      // Create a download link for the returned image/blob
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `giftcard-qr-${sanitizedCardNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success("QR code downloaded successfully.");
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast.error("Failed to download QR code. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/giftcard/allissuedgiftcards")}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Issue New Gift Card
          </h1>
          <p className="text-gray-600 mt-1">
            Issue a new gift card with custom details
          </p>
        </div>
      </div>

      {/* Form */}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, resetForm }) => {
          // Store resetForm function so it can be accessed in modal
          if (formResetFn !== resetForm) {
            setFormResetFn(() => resetForm);
          }
          return (
            <Form className="space-y-8">
              {/* Select Gift Card Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Select Gift Card</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Selected Gift Card</Label>
                    <Field name="selectedGiftCardId">
                      {({ meta }: any) => (
                        <div>
                          {selectedGiftCard ? (
                            <div className="p-3 border rounded-lg bg-gray-50 relative">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={
                                    selectedGiftCard.bannerImagePath ||
                                    "/placeholder.svg"
                                  }
                                  alt={selectedGiftCard.title}
                                  className="w-12 h-8 object-cover rounded"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">
                                    {selectedGiftCard.title}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {selectedGiftCard.description}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">
                                    {selectedGiftCard.amount.toFixed(2)}{" "}
                                    {localStorage.getItem("giftCardCurrency") ||
                                      "USD"}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveGiftCard(setFieldValue)
                                  }
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                              <p className="text-gray-500 text-sm">
                                No gift card selected
                              </p>
                            </div>
                          )}
                          {meta.touched && meta.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {meta.error}
                            </p>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsGiftCardModalOpen(true)}
                    className="w-full"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    {selectedGiftCard ? "Change Gift Card" : "Select Gift Card"}
                  </Button>
                </CardContent>
              </Card>

              {/* Recipient Information Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Recipient Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="recipientName">Recipient Name *</Label>
                      <Field name="recipientName">
                        {({ field, meta, form }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="recipientName"
                              type="text"
                              placeholder="Enter recipient name"
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow only letters and spaces
                                if (/^[A-Za-z ]*$/.test(value)) {
                                  form.setFieldValue(field.name, value);
                                }
                              }}
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="recipientEmail">Recipient Email *</Label>
                      <Field name="recipientEmail">
                        {({ field, meta }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="recipientEmail"
                              type="email"
                              placeholder="Enter recipient email"
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  {/* Mobile Number Field */}
                  <div className="space-y-2">
                    <Label htmlFor="recipientMobile">Mobile Number *</Label>
                    <Field name="recipientMobile">
                      {({ field, meta }: any) => (
                        <div>
                          <Input
                            {...field}
                            id="recipientMobile"
                            type="tel"
                            placeholder="Enter mobile number"
                            className={
                              meta.touched && meta.error ? "border-red-500" : ""
                            }
                          />
                          {meta.touched && meta.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {meta.error}
                            </p>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </CardContent>
              </Card>

              {/* Gift Details Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Gift Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="personalMessage">Personal Message</Label>
                    <Field name="personalMessage">
                      {({ field, meta }: any) => (
                        <div>
                          <Textarea
                            {...field}
                            id="personalMessage"
                            placeholder="Write a personal message for the recipient..."
                            rows={4}
                            className={
                              meta.touched && meta.error ? "border-red-500" : ""
                            }
                          />
                          {meta.touched && meta.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {meta.error}
                            </p>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occasion">Occasion</Label>
                    <Field name="occasion">
                      {({ field, meta }: any) => (
                        <div>
                          <Input
                            {...field}
                            id="occasion"
                            type="text"
                            placeholder="e.g., Birthday, Anniversary, Holiday"
                            className={
                              meta.touched && meta.error ? "border-red-500" : ""
                            }
                          />
                          {meta.touched && meta.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {meta.error}
                            </p>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <div className="flex justify-center pt-6">
                <div className="flex items-center space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-8"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Issuing..." : "Issue Gift Card"}
                  </Button>
                </div>
              </div>

              {/* Gift Card Selection Modal */}
              <Dialog
                open={isGiftCardModalOpen}
                onOpenChange={setIsGiftCardModalOpen}
              >
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <div className="flex items-center justify-between gap-4 pr-6">
                      <DialogTitle>Select Gift Card</DialogTitle>
                      <div className="w-48">
                        <Select
                          value={
                            selectedMallId !== null
                              ? selectedMallId.toString()
                              : undefined
                          }
                          onValueChange={(value) => {
                            if (value === "clear") {
                              setSelectedMallId(null);
                              if (hasInitialFetchRef.current) {
                                setPage(0);
                                fetchGiftCards({
                                  page: 0,
                                  pageSize: rowsPerPage,
                                  mallId: null,
                                });
                              }
                            } else {
                              const newMallId = Number(value);
                              setSelectedMallId(newMallId);
                              if (hasInitialFetchRef.current) {
                                setPage(0);
                                fetchGiftCards({
                                  page: 0,
                                  pageSize: rowsPerPage,
                                  mallId: newMallId,
                                });
                              }
                            }
                          }}
                          disabled={isMallLoading || malls.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isMallLoading
                                  ? "Loading malls..."
                                  : "Select mall"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="!max-h-48 overflow-auto py-0">
                            <SelectItem
                              value="clear"
                              className="text-gray-500 italic"
                            >
                              Clear selection
                            </SelectItem>
                            {malls.length > 0 ? (
                              malls.map((mall) => (
                                <SelectItem
                                  key={mall.id}
                                  value={mall.id.toString()}
                                >
                                  {mall.name}
                                </SelectItem>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                {isMallLoading
                                  ? "Loading..."
                                  : "No malls found"}
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search gift cards..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Loading State */}
                    {isLoadingGiftCards || isMallLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading gift cards...</p>
                      </div>
                    ) : (
                      <>
                        {/* Gift Cards Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filteredGiftCards.map((card) => (
                            <Card
                              key={card.id}
                              className="cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() =>
                                handleGiftCardSelect(card, setFieldValue)
                              }
                            >
                              <CardContent className="p-4">
                                <div className="space-y-3">
                                  <img
                                    src={
                                      card.bannerImagePath || "/placeholder.svg"
                                    }
                                    alt={card.title}
                                    className="w-full h-24 object-cover rounded"
                                  />
                                  <div>
                                    <h3 className="font-medium text-sm">
                                      {card.title}
                                    </h3>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {card.description}
                                    </p>
                                    <p className="font-bold text-green-600 mt-2">
                                      {card.amount.toFixed(2)}{" "}
                                      {localStorage.getItem(
                                        "giftCardCurrency"
                                      ) || "USD"}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>

                        {filteredGiftCards.length === 0 &&
                          !isLoadingGiftCards && (
                            <div className="text-center py-8">
                              <p className="text-gray-500">
                                No gift cards found
                              </p>
                            </div>
                          )}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="mt-4 flex justify-between items-center">
                            {/* Rows per page selector */}
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-700">
                                Rows per page:
                              </span>
                              <Select
                                value={rowsPerPage.toString()}
                                onValueChange={(value) => {
                                  const newRowsPerPage = parseInt(value, 10);
                                  setRowsPerPage(newRowsPerPage);
                                  setPage(0);
                                  fetchGiftCards({
                                    page: 0,
                                    pageSize: newRowsPerPage,
                                    mallId: selectedMallId,
                                  });
                                }}
                              >
                                <SelectTrigger className="w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="5">5</SelectItem>
                                  <SelectItem value="10">10</SelectItem>
                                  <SelectItem value="20">20</SelectItem>
                                  <SelectItem value="30">30</SelectItem>
                                  <SelectItem value="50">50</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Page buttons */}
                            <PaginationControls
                              currentPage={page}
                              totalPages={totalPages}
                              onPrevHandler={() => handlePageChange(page - 1)}
                              onNextHandler={() => handlePageChange(page + 1)}
                              onPageChange={handlePageChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </Form>
          );
        }}
      </Formik>

      {/* Success Modal */}
      <Dialog
        open={isSuccessModalOpen}
        onOpenChange={(open) => {
          setIsSuccessModalOpen(open);
          if (!open) {
            // Clear form when modal is closed
            if (formResetFn) {
              formResetFn();
            }
            setSelectedGiftCard(null);
            setIssuedGiftCard(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              <span>Gift Card Issued Successfully!</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <p className="text-gray-600 text-center">
              Your gift card has been issued and is ready to use
            </p>

            {issuedGiftCard && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 border-2 border-blue-200">
                {/* Gift Card Display */}
                <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">
                  {/* Card Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-6 w-6 text-blue-600" />
                      <span className="font-semibold text-gray-800">
                        Gift Card
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">Valid</div>
                  </div>

                  {/* Card Number */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-500">Card Number</div>
                    <div className="font-mono text-xl font-bold text-gray-800">
                      {issuedGiftCard.issuedCardNumber || "N/A"}
                    </div>
                  </div>

                  {/* QR Code and Value */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="text-sm text-gray-500">Value</div>
                      <div className="text-2xl font-bold text-green-600">
                        {issuedGiftCard.amount.toFixed(2)}{" "}
                        {localStorage.getItem("giftCardCurrency") || "USD"}
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-20 h-20 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                        {issuedGiftCard.qrCode ? (
                          <img
                            src={issuedGiftCard.qrCode}
                            alt="Gift Card QR Code"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <QrCode className="h-10 w-10 text-gray-400" />
                        )}
                      </div>
                      {issuedGiftCard.qrCode && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center text-xs"
                          onClick={downloadQRCode}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download QR
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Issued For */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500 mb-1">Issued For</div>
                    <div className="font-medium text-gray-800">
                      {issuedGiftCard.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {issuedGiftCard.email}
                    </div>
                    <div className="text-sm text-gray-600">
                      {issuedGiftCard.mobileNo || ""}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  // Clear form when close button is clicked
                  if (formResetFn) {
                    formResetFn();
                  }
                  setSelectedGiftCard(null);
                  setIssuedGiftCard(null);
                }}
                className="px-6"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  navigate("/giftcard/allissuedgiftcards");
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6"
              >
                View All Gift Cards
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IssueGiftCard;
